import React, { useState, useEffect } from 'react';
import { SessionProvider, useSession } from '../contexts/SessionContext';
import { NotificationProvider } from '../contexts/NotificationContext';
import SessionGuard from '../components/session/SessionGuard';
import SessionStatus from '../components/session/SessionStatus';
import Sidebar from '../features/vendor/Sidebar';
import Navbar from '../features/vendor/Navbar';
import Login from '../features/auth/Login';
import Register from '../features/auth/Register';
import ForgotPassword from '../features/auth/ForgotPassword';
import ResetPassword from '../features/auth/ResetPassword';
import LocationSetup from '../features/vendor/LocationSetup';
import KYCUpload from '../features/vendor/KYCUpload';
import KYCProgress from '../features/vendor/KYCProgress';
import Dashboard from '../features/vendor/Dashboard';
import Products from '../features/vendor/Products';
import InventoryDashboard from '../features/vendor/InventoryDashboard';
import VendorKYC from '../features/vendor/VendorKYC';
import KYCDashboard from '../features/vendor/KYCDashboard';
import Payouts from '../features/vendor/Payouts';
import BankDetails from '../features/vendor/BankDetails';
import Settings from '../features/vendor/Settings';
import './App.css';

const AppContent = () => {
  const { isAuthenticated, user, login, logout } = useSession();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth <= 1024;
  });
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth <= 768;
  });
  const [currentPage, setCurrentPage] = useState('vendor-login');
  const [vendorData, setVendorData] = useState(null);

  // Update current page based on authentication status
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.type === 'vendor') {
        setCurrentPage('vendor-dashboard');
      }
    } else {
      setCurrentPage('vendor-login');
    }
  }, [isAuthenticated, user]);

  // Track viewport to detect mobile
  useEffect(() => {
    const updateViewportState = () => {
      if (typeof window === 'undefined') return;
      const mobile = window.innerWidth <= 768;
      const tabletOrBelow = window.innerWidth <= 1024;
      setIsMobile(mobile);
      setSidebarCollapsed(prev => {
        if (mobile) return true;
        if (!tabletOrBelow) return false;
        return prev;
      });
    };

    updateViewportState();
    window.addEventListener('resize', updateViewportState);
    return () => window.removeEventListener('resize', updateViewportState);
  }, []);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const shouldLock = isMobile && !sidebarCollapsed;
      document.body.style.overflow = shouldLock ? 'hidden' : '';
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    };
  }, [isMobile, sidebarCollapsed]);

  // Lightweight hash-based routing
  useEffect(() => {
    const applyHashRoute = () => {
      const hash = (window.location.hash || '').replace('#', '').replace('/', '');
      
      if (hash === 'vendor-login' || hash === '' || hash === 'vendor') {
        setCurrentPage('vendor-login');
        return;
      }
      if (hash === 'vendor-register') {
        setCurrentPage('vendor-register');
        return;
      }
      if (hash === 'forgot-password') {
        setCurrentPage('forgot-password');
        return;
      }
      if (hash === 'reset-password') {
        setCurrentPage('reset-password');
        return;
      }
      
      // Default unauthenticated route to vendor login
      if (!isAuthenticated) {
        setCurrentPage('vendor-login');
      }
    };
    applyHashRoute();
    window.addEventListener('hashchange', applyHashRoute);
    return () => window.removeEventListener('hashchange', applyHashRoute);
  }, [isAuthenticated]);

  const handleVendorLogin = (vendorData) => {
    login({
      email: vendorData.email,
      type: 'vendor'
    });
    
    // Store vendor-specific data
    localStorage.setItem('vendorData', JSON.stringify(vendorData));
    if (vendorData.token) {
      localStorage.setItem('token', vendorData.token);
    }
    
    // Set appropriate page based on vendor status
    const nextPage = vendorData.status === 'APPROVED' ? 'vendor-dashboard' : 'kyc-progress';
    setCurrentPage(nextPage);
  };

  const handleVendorRegister = (data) => {
    // Clear any existing vendor data
    setVendorData(null);
    // Redirect to login page after successful registration
    setCurrentPage('vendor-login');
  };

  const handleLocationSet = (locationData) => {
    setVendorData(prev => ({
      ...prev,
      location: locationData
    }));
    setCurrentPage('kyc-upload');
  };

  const handleLocationSkip = () => {
    setCurrentPage('kyc-upload');
  };

  const handleKYCComplete = (kycData) => {
    setVendorData(prev => ({
      ...prev,
      kyc: kycData
    }));
    setCurrentPage('kyc-progress');
  };

  const handleKYCSkip = () => {
    setCurrentPage('kyc-progress');
  };

  const handleKYCProgressComplete = () => {
    // Migrate to SessionContext login flow
    if (vendorData?.email) {
      login({ email: vendorData.email, type: 'vendor' });
    }
    setCurrentPage('vendor-dashboard');
  };

  const handleKYCEdit = (step) => {
    if (step === 'registration') {
      setCurrentPage('vendor-register');
    } else if (step === 'location') {
      setCurrentPage('location-setup');
    } else if (step === 'kyc') {
      setCurrentPage('kyc-upload');
    }
  };

  const handleSwitchToVendorRegister = () => {
    setCurrentPage('vendor-register');
  };

  const handleSwitchToVendorLogin = () => {
    setCurrentPage('vendor-login');
  };

  const handleSwitchToForgotPassword = () => {
    setCurrentPage('forgot-password');
  };

  const handleBackToLogin = () => {
    setCurrentPage('vendor-login');
  };

  const renderVendorContent = () => {
    const vendorData = JSON.parse(localStorage.getItem('vendorData') || '{}');
    const vendorId = vendorData.id;

    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'products':
        return <Products />;
      case 'inventory':
        return <InventoryDashboard />;
      case 'kyc':
        return vendorId ? <KYCDashboard vendorId={vendorId} /> : <VendorKYC />;
      case 'payouts':
        return <Payouts />;
      case 'bank-details':
        return <BankDetails />;
      case 'profile':
        return <Settings />;
      default:
        return <Dashboard />;
    }
  };

  // Show login/register pages if not authenticated
  if (!isAuthenticated) {
    if (currentPage === 'vendor-login') {
      return <Login onLogin={handleVendorLogin} onSwitchToRegister={handleSwitchToVendorRegister} onSwitchToForgotPassword={handleSwitchToForgotPassword} onSwitchToAdmin={() => {}} />;
    }
    if (currentPage === 'vendor-register') {
      return <Register onRegister={handleVendorRegister} onSwitchToLogin={handleSwitchToVendorLogin} />;
    }
    if (currentPage === 'location-setup') {
      return <LocationSetup onLocationSet={handleLocationSet} onSkip={handleLocationSkip} initialAddress={vendorData?.shopAddress} vendorId={vendorData?.id} />;
    }
    if (currentPage === 'kyc-upload') {
      return <KYCUpload onKYCComplete={handleKYCComplete} onSkip={handleKYCSkip} vendorId={vendorData?.id} />;
    }
    if (currentPage === 'kyc-progress') {
      return <KYCProgress vendorData={vendorData} onComplete={handleKYCProgressComplete} onEdit={handleKYCEdit} />;
    }
    if (currentPage === 'forgot-password') {
      return <ForgotPassword onBackToLogin={handleBackToLogin} />;
    }
    if (currentPage === 'reset-password') {
      return <ResetPassword onBackToLogin={handleBackToLogin} />;
    }
    // Fallback to vendor login
    return <Login onLogin={handleVendorLogin} onSwitchToRegister={handleSwitchToVendorRegister} onSwitchToForgotPassword={handleSwitchToForgotPassword} onSwitchToAdmin={() => {}} />;
  }

  // Render vendor portal
  return (
    <SessionGuard requiredUserType="vendor">
      <div className="app">
        <Sidebar 
          activeTab={activeTab} 
          setActiveTab={setActiveTab}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          onLogout={logout}
        />
        {isMobile && !sidebarCollapsed && (
          <div
            className="mobile-overlay active"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}
        <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Navbar 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            userEmail={user?.email}
            onToggleSidebar={() => setSidebarCollapsed(prev => !prev)}
            sidebarCollapsed={sidebarCollapsed}
          >
            <SessionStatus />
          </Navbar>
          <div className="content">
            {renderVendorContent()}
          </div>
        </div>
      </div>
    </SessionGuard>
  );
}

function App() {
  return (
    <SessionProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </SessionProvider>
  );
}

export default App;

